import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Repeat, Pause, Play, Loader2, CalendarClock } from "lucide-react";
import api from "../lib/api";

const FREQ_LABEL = { WEEKLY: "Every week", MONTHLY: "Every month", QUARTERLY: "Every quarter" };

export default function RecurringTab() {
  const [plans, setPlans] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get("/portal/recurring").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    try {
      await api.patch(`/portal/recurring/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Recurring order paused" : "Recurring order resumed");
      load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete recurring order for "${p.product_service}"?`)) return;
    try { await api.delete(`/portal/recurring/${p.id}`); toast.success("Recurring order deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (plans === null) return <div className="text-slate-400 py-10" data-testid="recurring-loading">Loading recurring orders...</div>;

  return (
    <div data-testid="portal-recurring-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-[14px] text-slate-500 max-w-xl">Set up repeat procurement — we automatically create a new RFQ on your schedule and quote it for you.</p>
        <button data-testid="add-recurring-button" onClick={() => setAdding(!adding)} className="btn-primary !py-2.5 !px-4 text-[13px]"><Plus size={15} /> New Recurring Order</button>
      </div>

      {adding && <RecurringForm onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}

      {plans.length === 0 && !adding && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="recurring-empty-state">
          <Repeat className="mx-auto text-slate-300" size={36} />
          <p className="mt-4 text-[15px] text-slate-500">No recurring orders yet. Perfect for supplies you buy every month — gloves, valves, filters, PPE.</p>
        </div>
      )}

      <div className="space-y-4">
        {plans.map((p) => (
          <div key={p.id} data-testid={`recurring-plan-${p.id}`} className={`bg-white rounded-2xl border p-6 ${p.active ? "border-slate-200" : "border-slate-200 opacity-70"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">{p.product_service}</span>
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${p.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`} data-testid={`recurring-status-${p.id}`}>
                    {p.active ? "ACTIVE" : "PAUSED"}
                  </span>
                </div>
                <div className="mt-1.5 text-[13.5px] text-slate-500 flex items-center gap-3 flex-wrap">
                  {p.quantity && <span>Qty {p.quantity} {p.unit}</span>}
                  <span className="inline-flex items-center gap-1.5"><Repeat size={13} /> {FREQ_LABEL[p.frequency] || p.frequency}</span>
                  <span className="inline-flex items-center gap-1.5"><CalendarClock size={13} /> Next RFQ: <strong className="text-[var(--pg-navy)]">{p.next_run_at}</strong></span>
                  {p.runs_count > 0 && <span>{p.runs_count} RFQ{p.runs_count > 1 ? "s" : ""} created{p.last_rfq_number ? ` (last: ${p.last_rfq_number})` : ""}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`recurring-toggle-${p.id}`} onClick={() => toggle(p)}
                  className={`inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg ${p.active ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}>
                  {p.active ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Resume</>}
                </button>
                <button data-testid={`recurring-delete-${p.id}`} onClick={() => remove(p)} title="Delete" className="text-slate-500 hover:text-red-600 p-2"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecurringForm({ onSaved, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ product_service: "", quantity: "", unit: "Each", frequency: "MONTHLY", start_date: today, specifications: "", delivery_location: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/recurring", f);
      toast.success("Recurring order created — the first RFQ will be generated on the start date");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create recurring order");
    } finally { setBusy(false); }
  };

  return (
    <form data-testid="recurring-form" onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 mb-5">
      <h3 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">New Recurring Order</h3>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-3"><label className="pg-label">Product / Service *</label><input data-testid="recurring-product-input" className="pg-input" placeholder="e.g. Nitrile exam gloves, size M" value={f.product_service} onChange={h("product_service")} required minLength={2} /></div>
        <div><label className="pg-label">Quantity</label><input data-testid="recurring-quantity-input" className="pg-input" placeholder="e.g. 500" value={f.quantity} onChange={h("quantity")} /></div>
        <div><label className="pg-label">Unit</label>
          <select data-testid="recurring-unit-input" className="pg-input" value={f.unit} onChange={h("unit")}>
            {["Each", "Box", "Case", "Pallet", "Set", "Gallons", "Pounds", "Kilograms"].map((u) => <option key={u}>{u}</option>)}
          </select></div>
        <div><label className="pg-label">Frequency *</label>
          <select data-testid="recurring-frequency-input" className="pg-input" value={f.frequency} onChange={h("frequency")}>
            <option value="WEEKLY">Weekly</option><option value="MONTHLY">Monthly</option><option value="QUARTERLY">Quarterly</option>
          </select></div>
        <div><label className="pg-label">First RFQ Date *</label><input data-testid="recurring-start-input" type="date" min={today} className="pg-input" value={f.start_date} onChange={h("start_date")} required /></div>
        <div className="sm:col-span-2"><label className="pg-label">Delivery Location</label><input data-testid="recurring-location-input" className="pg-input" placeholder="City, State" value={f.delivery_location} onChange={h("delivery_location")} /></div>
        <div className="sm:col-span-3"><label className="pg-label">Specifications</label><textarea data-testid="recurring-specs-input" rows={2} className="pg-input" value={f.specifications} onChange={h("specifications")} /></div>
        <div className="sm:col-span-3"><label className="pg-label">Notes</label><input data-testid="recurring-notes-input" className="pg-input" value={f.notes} onChange={h("notes")} /></div>
      </div>
      <div className="mt-5 flex gap-3">
        <button data-testid="recurring-form-submit" type="submit" disabled={busy} className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">
          {busy ? <Loader2 className="animate-spin" size={16} /> : "Create Recurring Order"}
        </button>
        <button type="button" data-testid="recurring-form-cancel" onClick={onCancel} className="px-4 rounded-lg border border-slate-200 text-[13px] font-[Poppins] font-semibold text-slate-500 hover:border-slate-400">Cancel</button>
      </div>
    </form>
  );
}
